import { useId, type ReactNode } from 'react';
import { cssVar } from '@going-once/tokens';
import { Checkbox } from '../../ui/checkbox';
import { ToggleChip } from '../../ui/filters';
import { Icon } from '../../ui/icons';
import { Box, Field, IconButton, Pressable, Stack, Text, visuallyHidden } from '../../ui/primitives';
import { Overline } from './composer-ui';
import type {
  ActionsPart,
  ChatMessage,
  ChatPart,
  ChoicesPart,
  DraftPart,
  ReviewPart,
  SummaryRow,
  TestPart,
} from './chat-model';

/**
 * Chat mode's screens (the Notifications composer, design export "Notifications Screen"): a
 * conversation, an input row and a Summary card.
 *
 * **Presentational, and nothing in it works.** The assistant behind Chat is not built. `disabled`
 * is the live page (everything inert); `frozen` is a gallery frame, which looks live but whose
 * handlers do nothing. Either way no callback is taken, so there is nothing to wire by mistake.
 */

const noop = (): void => undefined;
const noopChange = (_value: unknown): void => undefined;

/** A card on the sheet surface, the look of the draft, review and test cards. */
function PartCard({ children }: { children: ReactNode }) {
  return (
    <Box
      background="bg.raised"
      radius="md"
      padding="md"
      style={{ border: `1px solid ${cssVar('color.border.strong')}`, background: cssVar('color.bg.sheet') }}
    >
      <Stack gap="md">{children}</Stack>
    </Box>
  );
}

/** A value shown, not editable, with its counter: the draft's Title and Message. */
function ReadOnlyValue({ label, value, max }: { label: string; value: string; max: number }) {
  return (
    <Stack gap="xs">
      <Stack direction="row" justify="between" align="center">
        <Text step="label" tone="fg.muted">
          {label}{' '}
          <span aria-hidden="true">
            <Text step="label" tone="danger">
              *
            </Text>
          </span>
        </Text>
        <Text step="label" tone="fg.muted">
          {value.length}/{max}
        </Text>
      </Stack>
      <Box
        background="bg.base"
        radius="md"
        padding="sm"
        style={{ border: `1px solid ${cssVar('color.border.default')}` }}
      >
        <Text>{value}</Text>
      </Box>
    </Stack>
  );
}

/** A neutral block standing where a photo goes: the gallery carries no image files. */
function PhotoBlock({ size }: { size: 'sm' | 'md' }) {
  const edge = size === 'sm' ? cssVar('size.thumbnail') : `calc(2 * ${cssVar('size.thumbnail')})`;
  return (
    <div
      style={{
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        width: edge,
        height: size === 'sm' ? edge : `calc(1.5 * ${cssVar('size.thumbnail')})`,
        borderRadius: cssVar('radius.sm'),
        background: cssVar('color.bg.sunken'),
        border: `1px solid ${cssVar('color.border.default')}`,
      }}
    >
      <Text step="label" tone="fg.muted">
        Photo
      </Text>
    </div>
  );
}

function DraftCard({ part, inert }: { part: DraftPart; inert: boolean }) {
  return (
    <PartCard>
      <Text step="label" tone="fg.muted">
        Draft · Edit here or ask me to change it
      </Text>
      <ReadOnlyValue label="Title" value={part.title} max={part.titleMax} />
      <ReadOnlyValue label="Message" value={part.message} max={part.messageMax} />
      {part.photo ? (
        <Stack direction="row" gap="md" align="center">
          <PhotoBlock size="sm" />
          <Pressable variant="quiet" onPress={noop} disabled={inert}>
            Remove photo
          </Pressable>
        </Stack>
      ) : (
        <Stack direction="row">
          <Pressable variant="quiet" onPress={noop} disabled={inert}>
            <Stack direction="row" gap="xs" align="center">
              <Icon name="paperclip" />
              Attach photo
            </Stack>
          </Pressable>
        </Stack>
      )}
    </PartCard>
  );
}

function Choices({ part, inert }: { part: ChoicesPart; inert: boolean }) {
  return (
    // A disabled fieldset disables every chip inside it, which `ToggleChip` has no prop for.
    <fieldset
      disabled={inert}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: cssVar('space.sm'),
        margin: 0,
        padding: 0,
        border: 'none',
        minWidth: 0,
      }}
    >
      <legend style={visuallyHidden}>{part.label}</legend>
      {part.options.map((option) => (
        <ToggleChip key={option.id} label={option.label} on={part.selected === option.id} onPress={noop} />
      ))}
    </fieldset>
  );
}

function Review({ part }: { part: ReviewPart }) {
  return (
    <PartCard>
      <Overline>Review</Overline>
      <dl
        style={{
          margin: 0,
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr)',
          columnGap: cssVar('space.md'),
          rowGap: cssVar('space.sm'),
        }}
      >
        {part.rows.map((row) => (
          <div key={row.label} style={{ display: 'contents' }}>
            <dt>
              <Text step="label" tone="fg.muted">
                {row.label}
              </Text>
            </dt>
            <dd style={{ margin: 0 }}>
              <Text>{row.value}</Text>
            </dd>
          </div>
        ))}
      </dl>
      <Stack
        gap="xs"
        style={{ paddingTop: cssVar('space.md'), borderTop: `1px solid ${cssVar('color.border.default')}` }}
      >
        <Text>{part.reach}</Text>
        {part.waves ? (
          <Text step="label" tone="fg.muted">
            {part.waves}
          </Text>
        ) : null}
      </Stack>
    </PartCard>
  );
}

function TestCard({ part, inert }: { part: TestPart; inert: boolean }) {
  const toId = useId();
  const okId = useId();
  return (
    <>
      <PartCard>
        <Overline>Test</Overline>
        <Stack direction="row" gap="sm" align="end">
          <Box grow>
            <Field id={toId} label="Send test to" value={part.to} onChange={noopChange} disabled={inert} />
          </Box>
          <Pressable variant="quiet" onPress={noop} disabled={inert}>
            Send test
          </Pressable>
        </Stack>
        {part.tested ? (
          <Checkbox
            id={okId}
            label="I got the test and it looks right"
            checked={part.confirmed}
            onChange={noopChange}
            disabled={inert}
          />
        ) : null}
      </PartCard>
      <Stack direction="row" gap="md" align="center" wrap>
        <Pressable variant="primary" onPress={noop} disabled={inert || !part.confirmed}>
          Send now
        </Pressable>
        <Text step="label" tone="fg.muted">
          {part.hint}
        </Text>
      </Stack>
    </>
  );
}

function Actions({ part, inert }: { part: ActionsPart; inert: boolean }) {
  return (
    <Stack direction="row" gap="sm" wrap>
      {part.actions.map((action) => (
        <Pressable key={action.id} variant={action.variant} onPress={noop} disabled={inert || part.answered === true}>
          {action.label}
        </Pressable>
      ))}
    </Stack>
  );
}

function Part({ part, inert }: { part: ChatPart; inert: boolean }) {
  switch (part.kind) {
    case 'draft':
      return <DraftCard part={part} inert={inert} />;
    case 'choices':
      return <Choices part={part} inert={inert} />;
    case 'review':
      return <Review part={part} />;
    case 'test':
      return <TestCard part={part} inert={inert} />;
    case 'actions':
      return <Actions part={part} inert={inert} />;
  }
}

function Message({ message, inert }: { message: ChatMessage; inert: boolean }) {
  if (message.from === 'staff') {
    return (
      <div style={{ alignSelf: 'flex-end', maxWidth: '78%' }}>
        <Box
          background="bg.sunken"
          radius="md"
          padding="sm"
          style={{ border: `1px solid ${cssVar('color.border.default')}`, paddingInline: cssVar('space.md') }}
        >
          <Stack gap="sm">
            {message.photo ? <PhotoBlock size="md" /> : null}
            <Text>{message.text}</Text>
          </Stack>
        </Box>
      </div>
    );
  }
  return (
    <Stack direction="row" gap="sm" align="start" style={{ maxWidth: '92%' }}>
      {/* Decorative: the message says who it is from by where it sits. */}
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          width: cssVar('size.icon.button'),
          height: cssVar('size.icon.button'),
          borderRadius: cssVar('radius.sm'),
          background: cssVar('color.neutral.tint'),
          border: `1px solid ${cssVar('color.neutral.tint.border')}`,
          color: cssVar('color.fg.default'),
        }}
      >
        <Icon name="sparkles" />
      </span>
      <Stack gap="sm" grow style={{ paddingTop: cssVar('space.xs') }}>
        {message.text ? <Text>{message.text}</Text> : null}
        {message.parts?.map((part) => (
          <Part key={part.kind} part={part} inert={inert} />
        ))}
      </Stack>
    </Stack>
  );
}

function Summary({ rows, inert }: { rows: readonly SummaryRow[]; inert: boolean }) {
  return (
    <Box radius="md" padding="md" style={{ border: `1px solid ${cssVar('color.border.default')}` }}>
      <Stack gap="sm">
        <Overline>Summary</Overline>
        {rows.length === 0 ? (
          <Text tone="fg.muted">Fields show up here as the chat fills them in.</Text>
        ) : (
          rows.map((row) => (
            <div
              key={row.label}
              style={{
                display: 'grid',
                gridTemplateColumns: `${cssVar('size.column.sm')} minmax(0, 1fr) auto`,
                gap: cssVar('space.md'),
                alignItems: 'baseline',
                padding: `${cssVar('space.xs')} 0`,
                borderTop: `1px solid ${cssVar('color.border.default')}`,
              }}
            >
              <Text step="label" tone="fg.muted">
                {row.label}
              </Text>
              <Text>{row.value}</Text>
              <Pressable variant="quiet" onPress={noop} disabled={inert}>
                Edit<span style={visuallyHidden}> {row.label}</span>
              </Pressable>
            </div>
          ))
        )}
      </Stack>
    </Box>
  );
}

export function ChatPanel({
  messages,
  summary,
  draft = '',
  disabled = false,
  frozen = false,
}: {
  messages: readonly ChatMessage[];
  summary: readonly SummaryRow[];
  /** The input's text. */
  draft?: string;
  /** The live page: the whole panel is inert. */
  disabled?: boolean;
  /** A gallery frame: looks live, every handler does nothing. */
  frozen?: boolean;
}) {
  const inputId = useId();
  // A frozen frame is not disabled — it must look live — but its handlers are all no-ops.
  const inert = disabled;
  return (
    <Stack gap="lg">
      <div
        role="log"
        aria-live="polite"
        aria-label="Conversation"
        // The list scrolls inside a fixed room: two detail cards tall, from tokens.
        style={{
          maxHeight: `calc(2 * ${cssVar('size.card.detail')})`,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: cssVar('space.lg'),
          padding: `${cssVar('space.xs')} ${cssVar('space.xs')} ${cssVar('space.sm')}`,
        }}
      >
        {messages.map((message) => (
          <Message key={message.id} message={message} inert={inert} />
        ))}
      </div>
      <Stack direction="row" gap="sm" align="end">
        <Box grow>
          <label
            htmlFor={inputId}
            style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}
          >
            Message the assistant
          </label>
          <input
            id={inputId}
            type="text"
            value={draft}
            placeholder="Tell me what to send and who to send it to…"
            disabled={inert}
            readOnly={frozen}
            onChange={noop}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: cssVar('color.bg.base'),
              color: cssVar('color.fg.default'),
              border: `1px solid ${cssVar('color.border.default')}`,
              borderRadius: cssVar('radius.md'),
              padding: cssVar('space.sm'),
              minHeight: cssVar('size.target.min'),
              fontFamily: cssVar('family.base'),
              fontSize: cssVar('type.body.size'),
              ...(inert ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
            }}
          />
        </Box>
        <IconButton icon="paperclip" label="Attach photo" onPress={noop} disabled={inert} />
        <IconButton icon="arrow-up" label="Send to assistant" onPress={noop} disabled={inert || draft.trim() === ''} />
      </Stack>
      <Summary rows={summary} inert={inert} />
    </Stack>
  );
}
